import React, {FC, useEffect} from 'react';

import {RadioButton} from '@optimaxdev/design-system';
import {
    Icon,
    VisaD,
    MasterCardD,
    Jcb,
    Discover,
    AmericanExpress,
    CreditCard,
} from '@optimaxdev/icons';
import {VaultedPaymentMethod} from '@primer-io/checkout-web';
import classNames from 'classnames';
import {useDispatch, useSelector} from 'react-redux';

import {
    setAnalyticsMessage,
    setUiMessage,
    setVaultPaymentId as setVaultPaymentIdAction,
} from 'desktop/pages/checkout/store/reducers/primer/primer';
import {getVaultPaymentId} from 'desktop/pages/checkout/store/selectors/primer/primer';

import {PrimerError} from '../../../common/primerError/primerError';

import s from './savedCards.scss';

export type PropsType = {
    vaultedPaymentMethods: VaultedPaymentMethod[];
    primerError: string;
};

type SavedCardDetailsType = {
    endingNumbers: string;
    savedCardId: string;
    vendor: string;
};

const getSavedCardsDetails = (vaultedPaymentMethods: VaultedPaymentMethod[]) => {
    return vaultedPaymentMethods.map(vaultedCard => {
        return {
            endingNumbers: vaultedCard?.paymentInstrumentData?.last4Digits,
            savedCardId: vaultedCard?.id,
            vendor: vaultedCard?.paymentInstrumentData?.network.toLowerCase(),
        };
    });
};

const primerCreditCardIconsList: Record<string, React.JSX.Element> = {
    amex: <Icon height="24px" icon={AmericanExpress} label="amex" />,
    discover: <Icon height="24px" icon={Discover} label="discover" />,
    jcb: <Icon height="24px" icon={Jcb} label="jcb" />,
    mastercard: <Icon height="24px" icon={MasterCardD} label="mastercard" />,
    visa: <Icon height="24px" icon={VisaD} label="visa" />,
};

const SavedCardDetails = ({savedCard}: {savedCard: SavedCardDetailsType}) => {
    return (
        <div className={s.buttonContainer}>
            {primerCreditCardIconsList[savedCard.vendor] ?? (
                <Icon height="24px" icon={CreditCard} label="credit card" />
            )}

            <p className={s.paymentLevel}>
                <span>{savedCard.vendor}</span> - **** {savedCard.endingNumbers}
            </p>
        </div>
    );
};

/**
 * Show previous saved(vaulted) cards for primer payment method
 */
export const SavedCards: FC<PropsType> = ({primerError, vaultedPaymentMethods}) => {
    const dispatch = useDispatch();

    const vaultPaymentId = useSelector(getVaultPaymentId);

    const savedCardsDetails = getSavedCardsDetails(vaultedPaymentMethods);

    useEffect(() => {
        dispatch(setVaultPaymentIdAction(vaultedPaymentMethods.at(-1)?.id || ''));
    }, [dispatch, vaultedPaymentMethods]);

    return (
        <div className={s.wrapper}>
            {savedCardsDetails.length > 1 ? (
                savedCardsDetails.map(savedCard => {
                    return (
                        <React.Fragment key={savedCard.savedCardId}>
                            <div
                                className={classNames(s.creditCard, {
                                    [s.creditCardError]:
                                        vaultPaymentId === savedCard.savedCardId && primerError,
                                })}
                            >
                                <RadioButton
                                    onChange={() => {
                                        dispatch(setVaultPaymentIdAction(savedCard.savedCardId));
                                        dispatch(setUiMessage(''));
                                        dispatch(setAnalyticsMessage(''));
                                    }}
                                    isChecked={vaultPaymentId === savedCard.savedCardId}
                                    size="small"
                                    value="false"
                                    verticalAlign="center"
                                >
                                    <SavedCardDetails savedCard={savedCard} />
                                </RadioButton>
                            </div>
                            {vaultPaymentId === savedCard.savedCardId && (
                                <PrimerError primerError={primerError} />
                            )}
                        </React.Fragment>
                    );
                })
            ) : (
                <>
                    <div className={s.creditCard}>
                        <SavedCardDetails savedCard={savedCardsDetails[0]} />
                    </div>
                    <PrimerError primerError={primerError} />
                </>
            )}
        </div>
    );
};
